"use client";
import {
	DropdownMenuCheckboxItem,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import type { Decks } from "@cardheim/database";
import { type Visibility, visibilities } from "@cardheim/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { capitalCase } from "change-case";
import type { Selectable } from "kysely";
import { EyeIcon } from "lucide-react";
import { toast } from "sonner";
import { setDeckVisibility } from "../_server/setDeckVisibility";

interface Props {
	deck: Pick<Selectable<Decks>, "id" | "name" | "visibility">;
}

export const VisibilitySubMenu: React.FC<Props> = ({
	deck: { id, name, visibility },
}) => {
	const queryClient = useQueryClient();
	const { mutate } = useMutation({
		mutationFn: async (visibility: Visibility) => {
			await setDeckVisibility(id, visibility);
		},
		onError: () => {
			toast.error(`Something went wrong while updating visibility for ${name}`);
		},
		onSuccess: () => {
			toast.success(
				`Visibility updated to ${capitalCase(visibility || "")} for ${name}`,
			);
		},
		onSettled: async () => {
			queryClient.invalidateQueries({
				queryKey: ["decks", "personal"],
			});
		},
	});

	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger onClick={(e) => e.stopPropagation()}>
				<EyeIcon className="mr-2 h-4 w-4" />
				<span>Visibility</span>
			</DropdownMenuSubTrigger>
			<DropdownMenuPortal>
				<DropdownMenuSubContent>
					{visibilities.map((vis) => (
						<DropdownMenuCheckboxItem
							key={vis}
							checked={vis === visibility}
							onCheckedChange={() => mutate(vis as Visibility)}
							onClick={(e) => e.stopPropagation()}
						>
							{capitalCase(vis)}
						</DropdownMenuCheckboxItem>
					))}
				</DropdownMenuSubContent>
			</DropdownMenuPortal>
		</DropdownMenuSub>
	);
};
